import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumb, NavBar, SideNav } from "../../components";
import {
  fetchApplicationDetails,
  updateApplicationStatus,
  updateApplicationAssignee,
  createApplicationTask,
  updateApplicationTask,
} from "../../redux/slices/applications";
import { getEmployees } from "../../redux/slices/users";
import { fetchTemplates } from "../../redux/slices/templates";
import { timestampToDateString } from "../../config/firebase/index";
import { Timeline, Avatar, Dropdown } from "flowbite-react";
import {
  AiOutlineCalendar,
  AiOutlineAppstore,
  AiOutlineInfoCircle,
  AiOutlineUser,
  AiFillPlusCircle,
} from "react-icons/ai";
import TaskDetails from "./TaskDetails";
import EditTaskModal from "./EditTaskModal";
import SkeletonLoader from "./SkeletonLoader";

const applicationStatus = [
  { key: "in_the_queue", label: "Ready for Development" },
  { key: "in_progress", label: "In Progress" },
  { key: "awaiting_customer", label: "Awaiting Customer" },
  { key: "done", label: "Done" },
  { key: "closed", label: "Closed" },
];

const ViewApplication = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { selectedApplication, pageLoading, actionLoading } = useSelector(
    (state) => state.applications
  );
  const templatesStore = useSelector((state) => state.templates);
  const { employees } = useSelector((state) => state.users);
  const { tasks = [], activities = [] } = selectedApplication || {};
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const { user: loggedInUser } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchApplicationDetails(id));
    dispatch(getEmployees());
    dispatch(fetchTemplates());
  }, [dispatch, id]);

  const handleApplicationStatusChange = (status) => {
    // make sure the status is different from the current status
    if (status === selectedApplication.status) {
      return;
    }
    dispatch(updateApplicationStatus({ id, status, notify: true }));
  };

  const handleApplicationAssigneeChange = (employeeId) => {
    // make sure the status is different from the current status
    if (
      (selectedApplication.assignee && selectedApplication.assignee.id) ===
      employeeId
    ) {
      return;
    }
    dispatch(updateApplicationAssignee({ id, assignee: employeeId }));
  };

  const handleEditTaskClicked = (task) => {
    setSelectedTask(task);
    setEditModalOpen(true);
  };

  const handleSaveTask = async (task) => {
    try {
      if (task.id) {
        await dispatch(
          updateApplicationTask({
            taskData: task,
            applicationId: selectedApplication.id,
          })
        );
      } else {
        const resultAction = await dispatch(
          createApplicationTask({
            taskData: task,
            applicationId: selectedApplication.id,
          })
        );
        if (createApplicationTask.fulfilled.match(resultAction)) {
          setEditModalOpen(false);
        }
      }
    } catch {}
  };

  const handleCreateTaskClicked = () => {
    setSelectedTask({});
    setEditModalOpen(true);
  };

  return (
    <>
      <div className="antialiased bg-gray-50 dark:bg-gray-900">
        <NavBar />
        <SideNav />
        <EditTaskModal
          task={selectedTask}
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveTask}
          isLoading={actionLoading}
          templates={templatesStore.templates}
        />
        ;
        <main className="p-4 md:ml-64 h-auto pt-14">
          <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
            <Breadcrumb />
          </div>

          {pageLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              {/* Application Details Section */}
              <section className="mb-6 w-fit min-w-[400px]">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  Application Details
                </h2>
                {selectedApplication && (
                  <div className="mt-4 space-y-4 p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                    <div className="flex items-center space-x-4">
                      <Avatar
                        alt="User profile image"
                        img={selectedApplication.user.profileImage}
                        rounded
                        size="md"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                          {selectedApplication.user.fullName}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedApplication.user.email}
                        </p>
                      </div>
                    </div>
                    <hr />
                    <div className="flex items-center space-x-1">
                      <AiOutlineCalendar className="text-gray-600 dark:text-gray-400" />
                      <p className="text-sm text-gray-500 dark:text-white">
                        Created:{" "}
                        <strong>
                          {timestampToDateString(selectedApplication.createdAt)}
                        </strong>
                      </p>
                    </div>

                    <div className="flex items-center space-x-1">
                      <AiOutlineAppstore className="text-gray-600 dark:text-gray-400" />
                      <p className="text-sm text-gray-500 dark:text-white">
                        Service:{" "}
                        <strong>{selectedApplication.service.title}</strong>
                      </p>
                    </div>
                    {(selectedApplication.status !== "awaiting_confirmation" ||
                      (loggedInUser && loggedInUser.userType === "admin")) && (
                      <>
                        <div className="flex items-center space-x-2">
                          <AiOutlineInfoCircle className="text-gray-600 dark:text-gray-400" />
                          <p className="text-sm text-gray-500 dark:text-white">
                            Status:{" "}
                          </p>
                          <Dropdown
                            label={
                              applicationStatus.find(
                                (option) =>
                                  option.key === selectedApplication.status
                              )?.label || "Select Status"
                            }
                            size="xs"
                            color="purple"
                          >
                            {applicationStatus.map((option) => (
                              <Dropdown.Item
                                key={option.key}
                                onClick={() =>
                                  handleApplicationStatusChange(option.key)
                                }
                              >
                                {option.label}
                              </Dropdown.Item>
                            ))}
                          </Dropdown>
                        </div>
                        <div className="flex items-center space-x-2">
                          <AiOutlineUser className="text-gray-600 dark:text-gray-400" />
                          <p className="text-sm text-gray-500 dark:text-white">
                            Assignee:{" "}
                          </p>
                          <Dropdown
                            label={
                              (selectedApplication.assignee &&
                                selectedApplication.assignee.fullName) ||
                              "Select Assignee"
                            }
                            size="xs"
                            color="purple"
                          >
                            {employees.map((option) => (
                              <Dropdown.Item
                                key={option.id}
                                onClick={() =>
                                  handleApplicationAssigneeChange(option.id)
                                }
                              >
                                {option.fullName}
                              </Dropdown.Item>
                            ))}
                          </Dropdown>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </section>

              {/* Open Tasks Section */}
              <section className="mb-6">
                <div className="flex items-center">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                    Tasks
                  </h2>
                  <AiFillPlusCircle
                    onClick={() => handleCreateTaskClicked()}
                    className="ml-2 text-2xl text-purple-500 dark:text-gray-400 cursor-pointer hover:text-purple-600"
                  />
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                  {tasks && tasks.length > 0 ? (
                    tasks.map((task) => (
                      <TaskDetails
                        key={task.id}
                        task={task}
                        editTaskClicked={handleEditTaskClicked}
                      />
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No tasks available.</p>
                  )}
                </div>
              </section>

              {/* Activity Timeline Section */}
              <section className="">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-6">
                  Activity Timeline
                </h2>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
                  <Timeline className="">
                    {activities.length > 0 ? (
                      activities.map((activity, index) => (
                        <Timeline.Item key={index}>
                          <Timeline.Point />
                          <Timeline.Content>
                            <Timeline.Time>
                              {timestampToDateString(activity.createdAt)}
                            </Timeline.Time>
                            <Timeline.Title>{activity.title}</Timeline.Title>
                          </Timeline.Content>
                        </Timeline.Item>
                      ))
                    ) : (
                      <p className="text-gray-500 italic">
                        No activities recorded.
                      </p>
                    )}
                  </Timeline>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default ViewApplication;
